import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import EntityDialog from "@/components/EntityDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Warehouse, Plus, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Progress } from "@/components/ui/progress";

export default function Warehouses() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const whFields = [
    { name: "name", label: "Warehouse Name", required: true },
    { name: "code", label: "Code", required: true },
    { name: "city", label: "City" },
    { name: "capacity", label: "Capacity (units)", type: "number" },
    { name: "address", label: "Address", type: "textarea", full: true },
  ];

  const cols = [
    { key: "code", label: "Code", mono: true },
    { key: "name", label: "Warehouse", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "city", label: "City" },
    { key: "capacity", label: "Capacity", render: (r) => (r.capacity || 0).toLocaleString() },
    { key: "used", label: "Occupancy", render: (r) => {
      const pct = r.capacity ? Math.min(100, Math.round((r.used / r.capacity) * 100)) : 0;
      return <div className="w-32"><Progress value={pct} className="h-2" /><span className="text-xs text-slate-400">{pct}%</span></div>;
    } },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  const locCols = [
    { key: "code", label: "Location", mono: true },
    { key: "zone", label: "Zone" },
    { key: "aisle", label: "Aisle" },
    { key: "rack", label: "Rack" },
    { key: "bin", label: "Bin" },
    { key: "occupied", label: "Occupied", render: (r) => `${r.occupied}/${r.capacity}` },
  ];

  return (
    <div>
      <PageHeader title="Warehouses" subtitle="Sites, zones, racks & bin locations" icon={Warehouse}
        breadcrumb={["Home", "Warehouses"]}
        actions={can("warehouses.create") && (
          <Button data-testid="add-warehouse-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1.5" />Add Warehouse
          </Button>
        )} />

      <Tabs defaultValue="warehouses">
        <TabsList className="mb-4">
          <TabsTrigger value="warehouses" data-testid="tab-warehouses">Warehouses</TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">Bin Locations</TabsTrigger>
        </TabsList>
        <TabsContent value="warehouses">
          <DataTable endpoint="/warehouses" columns={cols} testId="warehouses-table" refreshKey={refresh}
            actions={(row) => can("warehouses.update") && (
              <button data-testid={`edit-wh-${row.id}`} onClick={() => { setEditing(row); setOpen(true); }} className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
            )} />
        </TabsContent>
        <TabsContent value="locations">
          <DataTable endpoint="/locations" columns={locCols} testId="locations-table" />
        </TabsContent>
      </Tabs>

      <EntityDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Warehouse" : "New Warehouse"}
        fields={whFields} endpoint="/warehouses" initial={editing} onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}
